import { getAccountScopedStore, migrateDefaultStoreToAccount, } from '#pplx/frontend/browser-storage/accountScopedIDB.js';
import { getActiveAccountId } from '#pplx/frontend/browser-storage/activeAccount.js';
import { QUERY_CACHE_KEY } from '#pplx/frontend/react-query/consts.js';
import { clear } from 'idb-keyval';
import { memoize } from 'lodash-es';
const NO_ACCOUNT = '__no_account__';
/**
 * The idb-keyval store the React Query cache persists to, scoped to the active account.
 *
 * Memoized **by the active account id**, not once: bootstrap activation can set the account
 * *after* the first persist call (the async session path), so a plain `once` would cache the
 * default store forever and route every later write there. Keying on the account re-resolves
 * when it flips from none→primary, while still opening only one IndexedDB connection per account.
 * `undefined` means no active account — idb-keyval falls back to its default store (unchanged).
 */
export const getQueryCacheStore = memoize(() => getAccountScopedStore(QUERY_CACHE_KEY), () => getActiveAccountId() ?? NO_ACCOUNT);
/**
 * Seeds an account's persisted React Query cache from the pre-activation default store, then
 * clears the default store. The IndexedDB analog of the localStorage migration: at first
 * activation the default store holds the pre-multiaccount data, so without this the next load
 * (which reads the account-scoped store) would start cold.
 *
 * Move, not copy: we clear the default store after seeding so a later account's activation can't
 * re-migrate this account's residual data into *its* store (e.g. sign out of A, sign into B as
 * the new primary → B's activation would otherwise copy A's leftover default-store data). The
 * tradeoff is that disabling multi-account starts the cache cold; that's an acceptable one-time
 * cost on a rare rollback versus a cross-account data leak.
 */
export async function migrateQueryCacheToAccount(accountId) {
    const target = getAccountScopedStore(QUERY_CACHE_KEY, accountId);
    if (!target) {
        return;
    }
    await migrateDefaultStoreToAccount(target);
    await clear();
}
//# sourceMappingURL=queryCacheStore.js.map